import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider,
} from "@mui/material";
import { Person, Save, ArrowBack } from "@mui/icons-material";
import { authService } from "../services/authService";
import { updateUserProfile } from "../store/slices/authSlice";

const ProfilePage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const [username, setUsername] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [initialLoading, setInitialLoading] = useState(true);

  useEffect(() => {
    const loadUserData = async () => {
      if (user?.uid) {
        try {
          const userData = await authService.getUserData(user.uid);
          if (userData?.username) {
            setUsername(userData.username);
          }
        } catch (error) {
          console.error("Errore nel caricamento dati utente:", error);
        }
      }
      setInitialLoading(false);
    };

    loadUserData();
  }, [user]);

  const handleSaveUsername = async () => {
    if (!username.trim()) {
      setError("L'username non può essere vuoto");
      return;
    }

    if (username.length < 3) {
      setError("L'username deve essere di almeno 3 caratteri");
      return;
    }

    if (username.length > 20) {
      setError("L'username non può superare i 20 caratteri");
      return;
    }

    // Verifica che contenga solo caratteri alfanumerici e underscore
    if (!/^[a-zA-Z0-9_]+$/.test(username)) {
      setError("L'username può contenere solo lettere, numeri e underscore");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      await authService.updateUserProfile(user.uid, {
        username: username.trim(),
      });

      // Aggiorna lo stato Redux
      dispatch(
        updateUserProfile({
          username: username.trim(),
        })
      );

      setSuccess("Username salvato con successo!");
    } catch (error) {
      setError(`Errore nel salvataggio: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleUsernameChange = (e) => {
    const value = e.target.value;
    setUsername(value);
    setError("");
    setSuccess("");
  };

  if (initialLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 600, mx: "auto", p: 3 }}>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate("/")}
        sx={{ mb: 3 }}
      >
        Torna alla Home
      </Button>

      <Paper elevation={3} sx={{ p: 4 }}>
        <Box display="flex" alignItems="center" mb={3}>
          <Person sx={{ mr: 2, fontSize: 32, color: "primary.main" }} />
          <Typography variant="h4" component="h1">
            Profilo Utente
          </Typography>
        </Box>

        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Informazioni Account
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Email: {user?.email}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Nome: {user?.username || "Non specificato"}
            </Typography>
          </CardContent>
        </Card>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>
          Username
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          L'username verrà mostrato al posto dell'email in tutta l'applicazione.
          Può contenere solo lettere, numeri e underscore (3-20 caratteri).
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <TextField
          fullWidth
          label="Username"
          value={username}
          onChange={handleUsernameChange}
          placeholder="Inserisci il tuo username"
          helperText="3-20 caratteri, solo lettere, numeri e underscore"
          sx={{ mb: 3 }}
          disabled={loading}
        />

        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} /> : <Save />}
          onClick={handleSaveUsername}
          disabled={loading || !username.trim()}
          size="large"
        >
          {loading ? "Salvando..." : "Salva Username"}
        </Button>
      </Paper>
    </Box>
  );
};

export default ProfilePage;
